import { useEffect, useMemo, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import PdfConfirmModal from './components/modals/PdfConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePdfConfirmModal from './components/hooks/usePdfConfirmModal';
import usePermissions from './components/hooks/usePermissions';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';
import { formatDateView } from './utils/dateUtils';
import { formatCurrencyView } from './utils/currencyUtils';
import PagarDespesaModal from './components/modalServices/PagarDespesaModal';
import DespesaModal from './components/modalServices/DespesaModal';
import AnularDespesaModal from './components/modalServices/AnularDespesaModal';
import PageCard from './components/PageCard';

function Despesas() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('despesas');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  // Campo único para pesquisa geral
  const [filtroGeral, setFiltroGeral] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [pagamentosOriginais, setPagamentosOriginais] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });

  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [selectedPagamento, setSelectedPagamento] = useState(null);
  const [showAnularModal, setShowAnularModal] = useState(false);
  const [despesaToAnular, setDespesaToAnular] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDespesaModal, setShowDespesaModal] = useState(false);
  const [despesaToEdit, setDespesaToEdit] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);

  // Função para abrir PDF no modal PdfViewer
  const openPdfInViewer = (pdfUrl, title = 'Relatório') => {
    localStorage.setItem('pdfViewerData', JSON.stringify({ url: pdfUrl, title }));
    const event = new CustomEvent('pdfViewerUpdate', { detail: { url: pdfUrl, title } });
    window.dispatchEvent(event);
  };

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DESPESA}/all`, {
        params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const dadosRecebidos = Array.isArray(result.data) ? result.data : [];
        console.log('dadosRecebidos:', dadosRecebidos);
        setPagamentosOriginais(dadosRecebidos);
      })
      .catch(error => {
        console.error('Erro ao obter registros:', error);
        setPagamentosOriginais([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate, podeAbrir, carregandoPermissoes]);

  // Filtro dos dados (deve vir antes do uso em paginação)
  // Função para converter data YYYY-MM-DD <-> DD/MM/YYYY
  function normalizeDate(dateStr) {
    if (!dateStr) return '';
    // Se vier no formato YYYY-MM-DD, retorna DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    // Se vier no formato DD/MM/YYYY, retorna YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split('/');
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  }

  const pagamentosFiltrados = [...pagamentosOriginais].filter(c => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();
    // Favorecido
    const nomeFav = `${c.nomeFavorecido || ''}`.toLowerCase();
    // Valor
    const valorStr =
      c.valor !== undefined && c.valor !== null
        ? String(c.valor).replace('.', ',')
        : '';
    // Datas
    const dataElabStr = c.dataElaboracao || '';
    const dataPagStr = c.dataPagamento || '';
    const dataElabNorm = normalizeDate(dataElabStr);
    const dataPagNorm = normalizeDate(dataPagStr);
    const termoNorm = normalizeDate(termo);
    // Pesquisa em todos os campos
    if (
      nomeFav.includes(termo) ||
      valorStr.toLowerCase().includes(termo) ||
      dataElabStr.toLowerCase().includes(termo) ||
      dataPagStr.toLowerCase().includes(termo) ||
      dataElabNorm.toLowerCase().includes(termo) ||
      dataPagNorm.toLowerCase().includes(termo) ||
      dataElabStr.toLowerCase().includes(termoNorm) ||
      dataPagStr.toLowerCase().includes(termoNorm)
    ) {
      return true;
    }
    return false;
  });

  // Corrigir paginação para refletir o total de itens filtrados
  const totalItems = pagamentosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { pdfModalData, showPdfModal, closePdfModal, handleConfirm } =
    usePdfConfirmModal();

  // Função utilitária para validar e abrir PDF
  const validateAndOpenPdf = (response, title = 'Relatório') => {
    // Verificar status da resposta
    if (response.status !== 200) {
      throw new Error(`Erro no servidor: ${response.status}`);
    }

    // Verificar se há dados na resposta
    if (!response.data || response.data.size === 0) {
      throw new Error('PDF não foi gerado corretamente - arquivo vazio');
    }

    // Verificar se é realmente um PDF (verificação adicional)
    const contentType = response.headers['content-type'];
    if (contentType && !contentType.includes('application/pdf')) {
      console.warn('Tipo de conteúdo inesperado:', contentType);
    }

    // Se chegou até aqui, o PDF está válido
    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    openPdfInViewer(pdfUrl, title);
    return true;
  };
  // Ordena por idDespesa (decrescente por padrão)
  const pagamentosOrdenados = pagamentosFiltrados.sort(
    (a, b) => b.idDespesa - a.idDespesa
  );
  const selectedPagamentos = pagamentosOrdenados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const handleSelectRecord = record => {
    const recordKey = `${record.ano}-${record.idDespesa}-${
      record.idPrograma || 'null'
    }-${record.idAcao || 'null'}-${record.cnpjAssociacao}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
    }
  };

  useEffect(() => {
    if (!pendingSelection) {
      setSelectedRecord(null);
    }
  }, [pagamentosOriginais, pendingSelection]);

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  const handleDespesaSaveSuccess = (savedDespesa, isEdit = false) => {
    setAtualizar(prev => prev + 1);

    if (savedDespesa) {
      const selectionData = {
        idDespesa: savedDespesa.idDespesa,
        ano: savedDespesa.ano || new Date().getFullYear(),
        cnpjAssociacao: savedDespesa.cnpjAssociacao || cnpj,
        idPrograma: savedDespesa.idPrograma,
        idAcao: savedDespesa.idAcao,
        isEdit: isEdit,
        isNewRecord: savedDespesa.isNewRecord || false,
      };
      setPendingSelection(selectionData);

      // Após salvar, sempre volta para a primeira página
      setTimeout(() => {
        if (setPagination && setPagination.onGoToPage) {
          setPagination.onGoToPage(1);
        }
      }, 500);
    }
  };

  useEffect(() => {
    if (pendingSelection && pagamentosOriginais.length > 0) {
      let foundRecord = null;
      if (
        pendingSelection.idDespesa &&
        pendingSelection.idDespesa !== '' &&
        !pendingSelection.isNewRecord
      ) {
        foundRecord = pagamentosOriginais.find(item => {
          const matches =
            item.idDespesa === pendingSelection.idDespesa &&
            item.ano === pendingSelection.ano &&
            item.cnpjAssociacao === pendingSelection.cnpjAssociacao;

          return matches;
        });
      }

      if (
        !foundRecord &&
        (!pendingSelection.isEdit || pendingSelection.isNewRecord)
      ) {
        const sortedRecords = [...pagamentosOriginais].sort(
          (a, b) => b.idDespesa - a.idDespesa
        );

        if (sortedRecords.length > 0) {
          foundRecord = sortedRecords[0];
        }
      }

      if (foundRecord) {
        const recordKey = `${foundRecord.ano}-${foundRecord.idDespesa}-${foundRecord.idPrograma}-${foundRecord.idAcao}-${foundRecord.cnpjAssociacao}`;

        const recordIndex = pagamentosOriginais.findIndex(
          item =>
            `${item.ano}-${item.idDespesa}-${item.idPrograma}-${item.idAcao}-${item.cnpjAssociacao}` ===
            recordKey
        );

        if (recordIndex !== -1) {
          const targetPage = Math.floor(
            recordIndex / setPagination.rowsPerPage
          );
          const targetPageOneIndexed = targetPage + 1;

          setSelectedRecord({ key: recordKey, data: foundRecord });

          if (targetPageOneIndexed !== setPagination.currentPage) {
            setPagination.onGoToPage(targetPageOneIndexed);
          }
        } else {
          setSelectedRecord({ key: recordKey, data: foundRecord });
        }

        setPendingSelection(null);
      } else {
        setPendingSelection(null);
      }
    }
  }, [pagamentosOriginais, pendingSelection, setPagination]);

  function handleDelete(c) {
    if (!c) return;

    // Verificar se o programa está encerrado
    if (c.idPrograma && !isProgramaAtivo(c.idPrograma)) {
      showDefaultModal(
        'error',
        'Este programa está ENCERRADO. Não é possível excluir despesas de programas encerrados.'
      );
      return;
    }

    axios
      .delete(
        `${API_BASE_URL.DESPESA}/delete/${c.cnpjAssociacao}/${c.ano}/${c.idDespesa}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        setAtualizar(prev => prev + 1);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
        setSelectedRecord(null);
      })
      .catch(error => {
        console.error('Erro ao excluir registro:', error);
        showDefaultModal('error', 'Erro ao excluir o registro.');
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setDespesaToEdit(record);
      setShowDespesaModal(true);
    }
  };

  const handleNewDespesa = () => {
    setDespesaToEdit(null);
    setShowDespesaModal(true);
  };

  const handlePaySelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setSelectedPagamento(record);
      setShowPagamentoModal(true);
    }
  };

  const handleAnularSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setDespesaToAnular(record);
      setShowAnularModal(true);
    }
  };

  const openFichaDespesa = async despesa => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.RELATORIO}/ficha_despesa_individual`,
        {
          params: {
            cnpjAssociacao: despesa.cnpjAssociacao,
            id: despesa.idDespesa,
            ano: despesa.ano,
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      validateAndOpenPdf(response, 'Ficha de Despesa Individual');
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      showDefaultModal('error', 'Falha ao gerar o relatório.');
      throw error; // Re-lança para o modal saber que falhou
    }
  };

  const openFichaPagamento = async pagamento => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.RELATORIO}/ficha_pagamento_individual`,
        {
          params: {
            cnpjAssociacao: pagamento.cnpjAssociacao,
            id: pagamento.idDespesa,
            ano: pagamento.ano,
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      validateAndOpenPdf(response, 'Ficha de Pagamento Individual');
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      showDefaultModal('error', 'Falha ao gerar o relatório.');
      throw error; // Re-lança para o modal saber que falhou
    }
  };

  // Funções com modal de confirmação
  const handlePrintFichaDespesa = () => {
    const record = getSelectedRecord();
    if (record) {
      showPdfModal(
        'Ficha de Despesa Individual',
        `Gerar ficha de despesa para o registro ${record.idDespesa} (${record.ano})?`,
        () => openFichaDespesa(record)
      );
    }
  };

  const handlePrintFichaPagamento = () => {
    const record = getSelectedRecord();
    if (record) {
      showPdfModal(
        'Ficha de Pagamento Individual',
        `Gerar ficha de pagamento para o registro ${record.idDespesa} (${record.ano})?`,
        () => openFichaPagamento(record)
      );
    }
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Despesas (Cadastradas / Pagas)"
      icon="bi-receipt"
      scrollable={false}
      footer={totalItems > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Despesas.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">

          {/* Cadastro */}
          <button
            onClick={handleNewDespesa}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar despesas' : 'Nova despesa'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>

          <div className="vr mx-1" />

          {/* Operações financeiras */}
          <button
            className="btn btn-info btn-sm d-inline-flex align-items-center"
            onClick={handlePaySelected}
            disabled={!podeEditar || !hasSelectedRecord}
            title={!podeEditar ? 'Você não tem permissão para pagar' : !hasSelectedRecord ? 'Selecione uma despesa' : 'Pagar despesa selecionada'}
          >
            <i className="bi bi-cash-coin me-1" />
            Pagar
          </button>
          <button
            className="btn btn-warning btn-sm d-inline-flex align-items-center"
            onClick={handleAnularSelected}
            disabled={!podeEditar || !hasSelectedRecord}
            title={!podeEditar ? 'Você não tem permissão para anular' : !hasSelectedRecord ? 'Selecione uma despesa' : 'Anular despesa selecionada'}
            style={{ color: '#5a3e00' }}
          >
            <i className="bi bi-slash-circle me-1" />
            Anular
          </button>

          <div className="vr mx-1" />

          {/* Relatórios */}
          <div className="btn-group" role="group">
            <button
              className="btn btn-secondary btn-sm dropdown-toggle d-inline-flex align-items-center"
              type="button"
              data-bs-toggle="dropdown"
              disabled={!podeExportar || !hasSelectedRecord}
              title={!podeExportar ? 'Você não tem permissão para exportar relatórios' : !hasSelectedRecord ? 'Selecione um registro' : 'Imprimir relatórios individuais'}
            >
              <i className="bi bi-printer me-1" />
              Relatórios
            </button>
            <ul className="dropdown-menu shadow-sm" style={{ zIndex: 1050 }}>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handlePrintFichaPagamento}
                >
                  <i className="bi bi-file-earmark-pdf me-2 text-danger" />
                  Ficha de Pagamento
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handlePrintFichaDespesa}
                >
                  <i className="bi bi-file-earmark-pdf me-2 text-danger" />
                  Ficha de Despesa
                </button>
              </li>
            </ul>
          </div>

          <div className="vr mx-1" />

          {/* Exclusão — ação destrutiva separada */}
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          {/* Pesquisa alinhada à direita */}
          <div className="ms-auto d-flex align-items-center">
            <div
              className="input-group"
              style={{ maxWidth: 300, height: 31 }}
            >
              <span
                className="input-group-text bg-white border-end-0 px-2"
                style={{ height: 31 }}
              >
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{
                  textTransform: 'uppercase',
                  height: 31,
                  fontSize: 13,
                }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>

        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                {/* coluna removida: seleção única */}
                <th className="px-2 align-middle" style={{ width: '4%' }}>
                  Nº
                </th>
                <th className="px-2 align-middle">Programa / Ação</th>
                <th className="px-2 align-middle">Fonte de Recursos</th>
                <th className="px-2 align-middle">Nome do Favorecido</th>
                <th className="px-2 align-middle">Nota Fiscal</th>
                <th className="px-2 align-middle">Data de Elaboração</th>
                <th className="px-2 align-middle">Data de Pagamento</th>
                <th className="px-2 align-middle">Forma de Pagamento</th>
                <th className="px-2 align-middle">Nº Conta</th>
                <th className="px-2 align-middle text-end">Valor</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedPagamentos.map(c => {
                  const recordKey = `${c.ano}-${c.idDespesa}-${c.idPrograma}-${c.idAcao}-${c.cnpjAssociacao}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;

                  // Corrige detecção de registro anulado
                  const isAnulada =
                    c.dataAnulacao !== null &&
                    c.dataAnulacao !== undefined &&
                    String(c.dataAnulacao).trim() !== '';
                  const shouldHighlight =
                    !isAnulada && c.idAcao != null && c.idItem == null;

                  let rowClass = '';
                  let rowStyle = {
                    cursor: 'pointer',
                  };
                  if (isSelected) {
                    rowClass = 'table-active border-primary border-2';
                    rowStyle.backgroundColor = '#e3f2fd';
                    rowStyle.borderLeft = '4px solid #0d6efd';
                    rowStyle.boxShadow = '0 2px 4px rgba(13, 110, 253, 0.2)';
                  } else if (isAnulada) {
                    rowClass = 'table-danger';
                    rowStyle.backgroundColor = '#f8d7da';
                    rowStyle.borderLeft = '4px solid #dc3545';
                    rowStyle.boxShadow = '0 2px 4px rgba(220, 53, 69, 0.2)';
                  } else if (shouldHighlight) {
                    rowClass = 'table-warning';
                    rowStyle.backgroundColor = '#fff3cd';
                    rowStyle.borderLeft = '4px solid #ffc107';
                    rowStyle.boxShadow = '0 2px 4px rgba(255, 193, 7, 0.2)';
                  }

                  return (
                    <tr
                      key={recordKey}
                      className={rowClass}
                      style={rowStyle}
                      onClick={() => handleSelectRecord(c)}
                    >
                      {/* coluna removida: seleção única */}
                      <td className="px-2">{c.idDespesa}</td>
                      <td className="px-2">
                        {c.descricaoPrograma && c.descricaoAcao
                          ? `${c.descricaoPrograma} / ${c.descricaoAcao}`
                          : c.descricaoPrograma
                          ? c.descricaoPrograma
                          : ''}
                      </td>
                      <td className="px-2">{c.descricaoFonteRecurso || ''}</td>
                      <td className="px-2">{`${c.idFavorecido} - ${c.nomeFavorecido}`}</td>
                      <td className="px-2">{c.notaFiscal}</td>
                      <td className="px-2">
                        {formatDateView(c.dataElaboracao)}
                      </td>
                      <td className="px-2">
                        {formatDateView(c.dataPagamento)}
                      </td>
                      <td className="px-2">{c.formaPagamento}</td>
                      <td className="px-2">{c.numConta || ''}</td>
                      <td className="px-2 text-end">
                        {formatCurrencyView(c.valor)}
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && pagamentosOriginais.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
      <ConfirmModal {...setConfirmModal} />
      <PdfConfirmModal
        show={pdfModalData.show}
        onClose={closePdfModal}
        onConfirm={handleConfirm}
        title={pdfModalData.title}
        message={pdfModalData.message}
        isLoading={pdfModalData.isLoading}
      />
      <PagarDespesaModal
        show={showPagamentoModal}
        onClose={() => setShowPagamentoModal(false)}
        despesa={selectedPagamento}
        onSaveSuccess={savedData => {
          if (savedData && typeof savedData === 'object') {
            handleDespesaSaveSuccess(savedData, true);
          } else {
            setAtualizar(prev => prev + 1);
          }
        }}
      />
      {/* Modal de Anulação de Despesa */}
      {showAnularModal && (
        <AnularDespesaModal
          show={showAnularModal}
          onClose={() => setShowAnularModal(false)}
          despesa={despesaToAnular}
          onSaveSuccess={savedData => {
            handleDespesaSaveSuccess(savedData, true);
            setShowAnularModal(false);
          }}
        />
      )}
      <DespesaModal
        show={showDespesaModal}
        onClose={() => setShowDespesaModal(false)}
        despesa={despesaToEdit}
        onSaveSuccess={savedData =>
          handleDespesaSaveSuccess(savedData, !!despesaToEdit)
        }
      />
    </PageCard>
  );
}

export default Despesas;
